import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';

/**
 * Service handling colorblind mode for accessibility.
 * When enabled, swaps all colors to colorblind-friendly alternatives.
 */
@Injectable({
  providedIn: 'root'
})
export class ColorblindService {
  private colorblindModeSubject = new ReplaySubject<boolean>(1);
  public colorblindMode$ = this.colorblindModeSubject.asObservable();
  private readonly COOKIE_NAME = 'colorblindMode';
  private readonly COOKIE_EXPIRY_DAYS = 365;

  constructor() {
    // Initialize on service creation
    this.getColorblindMode();
  }

  /**
   * Get current colorblind mode from cookie, if set. Otherwise, default to false.
   */
  public getColorblindMode(): boolean {
    const mode = this.getCookie(this.COOKIE_NAME);
    const isEnabled = mode === 'true';

    // Apply the class immediately
    const element = document.querySelector('html');
    if (element) {
      if (isEnabled) {
        element.classList.add('colorblind-mode');
      } else {
        element.classList.remove('colorblind-mode');
      }
    }

    // Emit the value
    this.colorblindModeSubject.next(isEnabled);
    return isEnabled;
  }

  /**
   * Set colorblind mode.
   * Adds CSS class to html element and stores preference in cookie.
   */
  public setColorblindMode(enabled: boolean) {
    const element = document.querySelector('html');
    if (element) {
      if (enabled) {
        element.classList.add('colorblind-mode');
      } else {
        element.classList.remove('colorblind-mode');
      }
    }
    this.setCookie(this.COOKIE_NAME, enabled.toString(), this.COOKIE_EXPIRY_DAYS);

    // Emit the change immediately so subscribers can react
    this.colorblindModeSubject.next(enabled);
  }

  /**
   * Toggles colorblind mode on/off.
   */
  public toggleColorblindMode() {
    const currentMode = this.getCookie(this.COOKIE_NAME) === 'true';
    this.setColorblindMode(!currentMode);
  }

  /**
   * Set a cookie
   */
  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  /**
   * Get a cookie value
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') {
        cookie = cookie.substring(1, cookie.length);
      }
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  }
}
